import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Col, Modal, Radio, Row, Switch, Table, message } from 'antd';
import Search from 'antd/lib/input/Search';
import { ColumnType } from 'antd/lib/table';
import {
  BOUserFragment,
  BOUserFragmentDoc,
  BlockedBOUsersDocument,
  BlockedBOUsersQuery,
  useBOUsersQuery,
  useBlockedBOUsersQuery,
  useClearBOUserDevicesMutation,
  useSetBOUserDeviceLimitationsMutation,
} from '../../apollo/api';

const PAGE_SIZE = 50;
const MAX_ENABLED_DEVICES = 3;

export default function Users() {
  const [viewMode, setViewMode] = useState<'search' | 'blocked'>('search');
  const isSearchMode = viewMode === 'search';
  const [searchKey, setSearchKey] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  const { loading: usersLoading, data: usersData } = useBOUsersQuery({
    variables: {
      searchKey,
      page,
      pageSize: PAGE_SIZE,
    },
    skip: !isSearchMode || searchKey === '',
    onError: (e) => message.error(e.message),
  });

  const { loading: blockedLoading, data: blockedData } = useBlockedBOUsersQuery({
    onError: (e) => message.error(e.message),
  });

  const loading = isSearchMode ? usersLoading : blockedLoading;
  const data = isSearchMode ? usersData?.users.users : blockedData?.blockedUsers;

  const columns: ColumnType<BOUserFragment>[] = [
    {
      title: 'Utente',
      key: 'info',
      render: (_, u) => (
        <>
          {(u.first_name || u.last_name) && (
            <div>
              <strong>
                {u.first_name} {u.last_name}
              </strong>
            </div>
          )}
          {u.email && (
            <div>
              <strong>{u.email}</strong>
            </div>
          )}
          <div>
            Dispositivi attivi: {u.enabledDevicesCount}
            {u.unlimitedActiveDevices ? ' (illimitati)' : ` / ${MAX_ENABLED_DEVICES}`}
          </div>
          <div>Prodotti Acquistati: {u.productsCount}</div>
        </>
      ),
    },
    {
      title: 'Dispositivi Illimitati',
      align: 'center',
      width: 180,
      key: 'limitations',
      render: (_, u) => <DeviceLimitations user={u} />,
    },
    {
      title: 'Reset Dispositivi',
      align: 'center',
      width: 180,
      key: 'reset',
      render: (_, u) => <DevicesReset user={u} />,
    },
    {
      align: 'center',
      width: 120,
      key: 'products',
      render: (_, u) => <Link to={(location) => `${location.pathname}/${u.id}`}>Prodotti...</Link>,
    },
  ];

  return (
    <>
      <h1>Utenti</h1>

      <div className="BackOffice">
        <div className="Content">
          <Row>
            <Col>
              <Badge count={blockedData?.blockedUsers?.length} style={{ zIndex: 1 }}>
                <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
                  <Radio.Button value="search">Ricerca utenti</Radio.Button>
                  <Radio.Button value="blocked">Utenti bloccati</Radio.Button>
                </Radio.Group>
              </Badge>
            </Col>
          </Row>
          {isSearchMode && (
            <>
              <br />
              <Row>
                <Col xs={24} xl={12}>
                  <Search
                    placeholder="Inserisci un nome, cognome o email"
                    enterButton="Cerca"
                    onSearch={(value) => {
                      setPage(1);
                      setSearchKey(value);
                    }}
                    loading={usersLoading}
                  />
                </Col>
              </Row>
            </>
          )}
          <br />
          <Row>
            <Col span={24}>
              <Table<BOUserFragment>
                columns={columns}
                dataSource={data}
                rowKey={(u) => u.id}
                loading={loading}
                pagination={
                  isSearchMode ? { onChange: setPage, pageSize: PAGE_SIZE, total: usersData?.users.resultCount } : false
                }
                scroll={{ x: true }}
              />
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
}

function DeviceLimitations({ user }: { user: BOUserFragment }) {
  const unlimited = user.unlimitedActiveDevices || false;

  const [setDeviceLimitations, { loading }] = useSetBOUserDeviceLimitationsMutation({
    onError: (e) => message.error(e.message),
  });

  const onDeviceLimitationsChange = useCallback(() => {
    setDeviceLimitations({
      variables: {
        userId: user.id,
        unlimitedActiveDevices: !unlimited,
        unlimitedDeviceActivations: !unlimited,
      },
      update(cache) {
        cache.writeFragment({
          id: cache.identify(user),
          fragment: BOUserFragmentDoc,
          data: { ...user, unlimitedActiveDevices: !unlimited, unlimitedDeviceActivations: !unlimited },
        });
      },
    });
  }, [user, unlimited, setDeviceLimitations]);

  return <Switch checked={unlimited} loading={loading} onChange={onDeviceLimitationsChange} />;
}

function DevicesReset({ user }: { user: BOUserFragment }) {
  const [clearDevices] = useClearBOUserDevicesMutation({
    onCompleted: () => message.success('Dispositivi resettati'),
    onError: (e) => message.error(e.message),
  });

  const onDevicesResetClick = useCallback(() => {
    Modal.confirm({
      title: 'Reset dispositivi',
      content:
        'Confermi di voler eliminare tutti i dispositivi registrati da questo utente? I dispositivi che usa verranno registrati di nuovo al prossimo contenuto aperto.',
      okText: 'Confermo',
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          await clearDevices({
            variables: { userId: user.id },
            update(cache) {
              cache.writeFragment({
                id: cache.identify(user),
                fragment: BOUserFragmentDoc,
                data: { ...user, enabledDevicesCount: 0 },
              });
              const existing = cache.readQuery<BlockedBOUsersQuery>({ query: BlockedBOUsersDocument });
              if (existing?.blockedUsers) {
                cache.writeQuery<BlockedBOUsersQuery>({
                  query: BlockedBOUsersDocument,
                  data: { blockedUsers: existing.blockedUsers.filter((u) => u.id !== user.id) },
                });
              }
            },
          });
        } catch (e) {}
      },
    });
  }, [user, clearDevices]);

  return <Button onClick={onDevicesResetClick}>Reset</Button>;
}
